import { Link, Outlet, useLocation, Navigate } from 'react-router-dom';
import { useAuthStore } from '@/context/authStore';
import ChangePasswordModal from '@/components/ChangePasswordModal';
import {
  LayoutDashboard,
  Building2,
  CreditCard,
  Wallet,
  Users,
  LogOut,
  Menu,
  X,
  Key,
  Settings,
  Mail,
  LucideIcon,
} from 'lucide-react';
import { useState } from 'react';
import clsx from 'clsx';

interface NavItem {
  name: string;
  href: string;
  icon: LucideIcon;
}

// Navegación del Admin Panel
const adminNavigation: NavItem[] = [
  { name: 'Dashboard', href: '/admin', icon: LayoutDashboard },
  { name: 'Veterinarias', href: '/admin/tenants', icon: Building2 },
  { name: 'Suscripciones', href: '/admin/subscriptions', icon: CreditCard },
  { name: 'Gastos', href: '/admin/expenses', icon: Wallet },
  { name: 'Usuarios', href: '/admin/users', icon: Users },
  { name: 'Recordatorios', href: '/admin/reminders', icon: Mail },
];

export default function AdminLayout() {
  const { user, logout } = useAuthStore();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);

  // Redirigir si no es superadmin
  if (user?.role !== 'superadmin') {
    return <Navigate to="/dashboard" replace />;
  }

  // El cambio de contraseña obligatorio ahora se maneja en /change-password
  // Este modal solo se usa para cambio voluntario de contraseña

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-20 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={clsx(
          'fixed inset-y-0 left-0 z-30 w-64 bg-slate-900 shadow-xl transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:relative',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-4 border-b border-slate-700 safe-top">
            <Link to="/admin" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center">
                <Settings className="h-5 w-5 text-white" />
              </div>
              <span className="text-lg font-bold text-white">ANIMAvet</span>
            </Link>
            <button
              className="lg:hidden p-2 rounded-md hover:bg-slate-800 text-slate-400"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Admin Badge */}
          <div className="px-4 py-3 border-b border-slate-700">
            <div className="flex items-center gap-2 px-3 py-2 bg-indigo-500/10 rounded-lg">
              <div className="w-2 h-2 bg-indigo-400 rounded-full animate-pulse"></div>
              <span className="text-sm font-medium text-indigo-300">Panel de Administración</span>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
            {adminNavigation.map((item) => {
              const isActive = location.pathname === item.href;
              const Icon = item.icon;

              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setSidebarOpen(false)}
                  className={clsx(
                    'flex items-center px-3 py-2.5 rounded-lg text-sm font-medium transition-all',
                    isActive
                      ? 'bg-indigo-500 text-white shadow-lg shadow-indigo-500/30'
                      : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                  )}
                >
                  <Icon className="h-5 w-5 mr-3" />
                  {item.name}
                </Link>
              );
            })}
          </nav>

          {/* User section */}
          <div className="border-t border-slate-700 p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="h-10 w-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                  <span className="text-white font-bold">
                    {user?.full_name.charAt(0).toUpperCase()}
                  </span>
                </div>
              </div>
              <div className="ml-3 flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">
                  {user?.full_name}
                </p>
                <p className="text-xs text-slate-400 truncate">{user?.email}</p>
              </div>
              <button
                onClick={() => setShowPasswordModal(true)}
                className="p-2 rounded-md hover:bg-slate-800 text-slate-400 hover:text-white"
                title="Cambiar contraseña"
              >
                <Key className="h-5 w-5" />
              </button>
              <button
                onClick={logout}
                className="p-2 rounded-md hover:bg-slate-800 text-slate-400 hover:text-white"
                title="Cerrar sesión"
              >
                <LogOut className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de cambio de contraseña (voluntario) */}
      <ChangePasswordModal
        isOpen={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
      />

      {/* Main content */}
      <div className="flex-1 flex flex-col min-h-screen overflow-auto">
        {/* Top bar (mobile) */}
        <div className="lg:hidden sticky top-0 z-10 bg-slate-900 shadow-lg safe-top">
          <div className="flex items-center justify-between h-16 px-4">
            <button
              className="p-2 rounded-md hover:bg-slate-800 text-slate-400"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="h-6 w-6" />
            </button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center">
                <Settings className="h-5 w-5 text-white" />
              </div>
              <span className="text-lg font-bold text-white">Admin</span>
            </div>
            <div className="w-10" />
          </div>
        </div>

        {/* Page content */}
        <main className="flex-1 p-4 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
